import React, { Suspense, useState, useEffect } from 'react';
import { Modal } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Spinner, PopupAd, ProfileForm } from '../components';
import { Theme } from '../res';
import StudentStack from '../Routers/StudentStack';
import { ImportantNotifications, isV2StudentRoute, StudentHeader, V2Root } from '../Routers/studentScreens';

const StudentLayout = ({ user }) => {
    // A finished exam is its results screen, which sits under the header like any page.
    const isGoingExamRunning = useSelector(({ userExam }) => userExam.onGoingExam && !userExam.isFinished);
    const { pathname } = useLocation();
    const isRedesigned = isV2StudentRoute(pathname);
    const [isCompleteProfileRequired, setIsCompleteProfileRequired] = useState(!user.contactNumber || !user.countryId);

    useEffect(() => {
        const element1 = document.getElementById('gtagscirpt1');
        if (element1) {
            element1.remove();
        }

        const element2 = document.getElementById('gtagscirpt2');
        if (element2) {
            element2.remove();
        }

        // Create the script element
        var script = document.createElement('script');
        script.async = true;
        script.src = 'https://www.googletagmanager.com/gtag/js?id=G-PBDHKG6NYY';
        script.setAttribute("id", "gtagscirpt1");

        // Append the script element to the document head
        document.head.appendChild(script);

        // Create the inline script element
        var inlineScript = document.createElement('script');

        inlineScript.setAttribute("id", "gtagscirpt2");
        inlineScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());

        gtag('config', 'G-PBDHKG6NYY');
        `;

        // Append the inline script element to the document head
        document.head.appendChild(inlineScript);
    });

    /*
     * The completion modal, which every signed-in page needs.
     *
     * A student whose profile is missing a phone or a country is asked for them
     * before anything else. That is the one thing the redesign does not
     * replace: the new profile page shows those fields read-only, because
     * changing them afterwards goes through support — so this is still the only
     * moment they can be filled in, and it has to keep working on both branches.
     */
    const completionModal = isCompleteProfileRequired
        ? <Modal
            closeIcon={null}
            open={isCompleteProfileRequired}
            content={<ProfileForm isCompleteProfile={isCompleteProfileRequired} onClose={() => setIsCompleteProfileRequired(false)} />}
            size='tiny'
            centered
            style={{ borderRadius: 16 }}
        />
        : <PopupAd />;

    /*
     * Unread important notifications open a dialogue by themselves. It replaces
     * the old red envelope (`MessagesButton`), whose semantic-ui popup came up
     * over the new pages in the old style.
     *
     * Not over the completion modal — two dialogues at once, and the one that
     * has to be answered first underneath — and not during an exam, where the
     * header is hidden for the same reason.
     */
    const importantNotifications = isCompleteProfileRequired || isGoingExamRunning
        ? null
        : <ImportantNotifications />;

    const page = (
        <Suspense fallback={<Spinner />}>
            <StudentStack />
        </Suspense>
    );

    /*
     * There is no sidebar. Every signed-in page gets the new header across the
     * top; the only question left is what sits under it.
     *
     * A redesigned page renders whole inside `V2Root`, which carries `dir`,
     * `lang`, the module's font and its stylesheet — without it the global
     * `rtl.css` wins on the bare `div` selector and the page comes up in Cairo,
     * laid out the wrong way round.
     *
     * Asked of the path, not of any state, for the reason `LandingLayout` gives
     * on its own auth branch: a flag raised in an effect answers one paint too
     * late, and the reader would watch one layout appear and then swap.
     *
     * The document scrolls on both branches: the header is `sticky`, and a
     * sticky element inside a scrolling box sticks to that box, not to the
     * viewport.
     */
    if (isRedesigned) {
        return (
            <V2Root>
                {completionModal}
                <StudentHeader />
                {page}
                {importantNotifications}
            </V2Root>
        );
    }

    /*
     * A page with no new design — the exams, the question banks and the exam
     * reviews — under the same header, but outside `V2Root`.
     *
     * Only the header is wrapped. `base.css` resets the font and the direction
     * on every descendant of `.v2-root`, and the exam screens, which lay
     * themselves out in both directions on purpose, would be reflowed by it.
     * So the header gets a root of its own, and that root is what sticks: the
     * header's own `sticky` has nowhere to travel inside a box its own height.
     * `!tw-min-h-0` undoes the root's full-screen height, and `smoothScroll`
     * stays off because the page underneath is not the module's to animate.
     *
     * A running exam takes the whole screen, as it did when this hid the
     * sidebar: the exam layouts are `tw-h-screen` and a header above them would
     * push their footer off the bottom.
     */
    return (
        <div style={{ position: 'relative', minHeight: '100vh', backgroundColor: isGoingExamRunning ? Theme.colors.WHITE : Theme.colors.GREY_10 }}>
            {completionModal}
            {!isGoingExamRunning && (
                <V2Root smoothScroll={false} className="tw-sticky tw-top-0 tw-z-40 !tw-min-h-0">
                    <StudentHeader />
                </V2Root>
            )}
            {page}
            {importantNotifications}
        </div>
    );
};

export default StudentLayout;
